import { useEffect, useState } from "react";
import { Store, Ticket, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const EMPTY_BATCH = { store_id: "", prefix: "", quantity: 100, label: "" };

function StoresPicker({ stores, value, onChange }) {
  const all = stores.length > 0 && stores.every((s) => value.includes(s.id));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="space-y-2" data-testid="contest-stores-section">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><Store className="h-4 w-4" /> Punti vendita coinvolti</Label>
        {stores.length > 0 && <button type="button" className="text-xs text-primary hover:underline" onClick={() => onChange(all ? [] : stores.map((s) => s.id))} data-testid="contest-stores-select-all">{all ? "Deseleziona tutte" : "Seleziona tutte"}</button>}
      </div>
      {stores.length === 0 ? <p className="text-xs text-muted-foreground">Nessun punto vendita registrato: creali da "Punti Vendita".</p> : (
        <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
          {stores.map((s) => (
            <label key={s.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs cursor-pointer ${value.includes(s.id) ? "border-primary/60 bg-primary/10" : "border-border bg-secondary/30"}`}>
              <input type="checkbox" checked={value.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`contest-store-${s.id}`} />
              <span className="truncate">{s.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function BatchRow({ b, i, stores, selected, onChange, onRemove }) {
  const opts = stores.filter((s) => selected.includes(s.id));
  return (
    <div className="grid grid-cols-[1fr_1fr_90px_80px_32px] gap-2 items-center" data-testid={`contest-batch-row-${i}`}>
      <Input value={b.label || ""} onChange={(e) => onChange({ ...b, label: e.target.value })} placeholder="Nome lotto (es. Volantino ottobre)" className="h-9 text-xs" data-testid={`contest-batch-label-${i}`} />
      <select value={b.store_id} onChange={(e) => onChange({ ...b, store_id: e.target.value })} className="h-9 rounded-md border border-border bg-background px-2 text-xs" data-testid={`contest-batch-store-${i}`}>
        <option value="">Generico – tutta la rete</option>
        {opts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <Input value={b.prefix} onChange={(e) => onChange({ ...b, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6) })} placeholder="Prefisso" className="h-9 text-xs font-mono-code" data-testid={`contest-batch-prefix-${i}`} />
      <Input type="number" min={1} max={5000} value={b.quantity} onChange={(e) => onChange({ ...b, quantity: e.target.value })} className="h-9 text-xs" data-testid={`contest-batch-qty-${i}`} />
      <Button size="icon" variant="ghost" className="h-9 w-8 text-muted-foreground hover:text-destructive" onClick={onRemove} data-testid={`contest-batch-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export function StoresAndCodesSection({ form, set, isEdit, contestId }) {
  const [stores, setStores] = useState([]);
  useEffect(() => {
    api.listStores().then(setStores).catch(() => setStores([]));
    if (isEdit && contestId) api.listContestStores(contestId).then((pools) => set("store_ids", pools.map((p) => p.store_id))).catch(() => {});
  }, [isEdit, contestId]); // eslint-disable-line react-hooks/exhaustive-deps
  const batches = form.initial_batches || [];
  const setBatch = (i, b) => set("initial_batches", batches.map((x, j) => (j === i ? b : x)));
  return (
    <div className="rounded-xl border border-border p-4 space-y-4">
      <StoresPicker stores={stores} value={form.store_ids || []} onChange={(v) => set("store_ids", v)} />
      {isEdit && <p className="text-[11px] text-muted-foreground">Togliendo la spunta a una sede ne rimuovi anche i limiti premi impostati nella tab Sedi.</p>}
      <div className="space-y-2" data-testid="contest-codes-section">
        <Label className="flex items-center gap-1.5"><Ticket className="h-4 w-4" /> {isEdit ? "Nuovi lotti codici da aggiungere" : "Codici concorso da generare"}</Label>
        <p className="text-xs text-muted-foreground">Ogni lotto può essere legato a un punto vendita (valido solo lì) oppure generico, valido su tutta la rete. I lotti compariranno nella tab "Lotti" per PDF ed Excel.</p>
        {batches.length > 0 && <div className="grid grid-cols-[1fr_1fr_90px_80px_32px] gap-2 text-[11px] text-muted-foreground px-0.5"><span>Nome lotto</span><span>Sede</span><span>Prefisso</span><span>Quantità</span><span /></div>}
        {batches.map((b, i) => <BatchRow key={b._key || `legacy-${i}`} b={b} i={i} stores={stores} selected={form.store_ids || []} onChange={(nb) => setBatch(i, nb)} onRemove={() => set("initial_batches", batches.filter((_, j) => j !== i))} />)}
        <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => set("initial_batches", [...batches, { ...EMPTY_BATCH, _key: crypto.randomUUID() }])} data-testid="contest-batch-add">
          <Plus className="h-4 w-4" /> Aggiungi lotto codici
        </Button>
      </div>
    </div>
  );
}
